import { HttpErrorResponse } from '@angular/common/http';
import type { BankingRegionVariant } from './banking-regions';

export type BankingErrorKind = 'network' | 'timeout' | 'auth' | 'validation' | 'server' | 'unknown';

export interface BankFieldError {
  source: string;
  code?: string;
  message: string;
}

export class BankingApiError extends Error {
  readonly kind: BankingErrorKind;
  readonly status?: number;
  readonly code?: string;
  readonly fieldErrors: BankFieldError[];

  constructor(
    message: string,
    kind: BankingErrorKind,
    options: { status?: number; code?: string; fieldErrors?: BankFieldError[] } = {},
  ) {
    super(message);
    this.name = 'BankingApiError';
    this.kind = kind;
    this.status = options.status;
    this.code = options.code;
    this.fieldErrors = options.fieldErrors ?? [];
  }
}

export type BankingFormFieldKey =
  | 'accountName'
  | 'sortCode'
  | 'routingNumber'
  | 'accountNumber'
  | 'iban'
  | 'category'
  | 'street'
  | 'city'
  | 'state'
  | 'zip'
  | 'region';

export const BANKING_FIELD_LABELS: Record<Exclude<BankingFormFieldKey, 'region'>, string> = {
  accountName: 'Account holder name',
  sortCode: 'Sort code',
  routingNumber: 'Routing number',
  accountNumber: 'Account number',
  iban: 'IBAN',
  category: 'Account type',
  street: 'Street address',
  city: 'City',
  state: 'State',
  zip: 'ZIP code',
};

/** Maps an API error `source` path to the form field it belongs to. */
export function fieldKeyForSource(
  source: string,
  variant: BankingRegionVariant,
): BankingFormFieldKey | null {
  switch (source.split('.').pop()) {
    case 'account_number':
    case 'iban':
      return variant === 'EUR' ? 'iban' : 'accountNumber';
    case 'account_routing_value1':
    case 'account_routing_type1':
      if (variant === 'GB') return 'sortCode';
      if (variant === 'US') return 'routingNumber';
      return null;
    case 'account_name':
      return 'accountName';
    case 'bank_account_category':
      return 'category';
    case 'street_address':
      return 'street';
    case 'city':
      return 'city';
    case 'state':
      return 'state';
    case 'postcode':
      return 'zip';
    default:
      return null;
  }
}

function parseErrorBody(body: unknown): {
  message?: string;
  code?: string;
  fieldErrors: BankFieldError[];
} {
  const nested =
    body && typeof body === 'object' && 'message' in body && typeof (body as { message: unknown }).message === 'object'
      ? ((body as { message: Record<string, unknown> }).message ?? null)
      : body;

  const source = nested && typeof nested === 'object' ? (nested as Record<string, unknown>) : null;
  const message =
    typeof source?.['message'] === 'string' && source['message']
      ? source['message']
      : typeof (body as { message?: unknown })?.message === 'string'
        ? ((body as { message: string }).message ?? undefined)
        : undefined;
  const code = typeof source?.['code'] === 'string' ? source['code'] : undefined;

  const rawErrors = Array.isArray((source?.['details'] as { errors?: unknown } | undefined)?.errors)
    ? ((source!['details'] as { errors: unknown[] }).errors)
    : [];
  const fieldErrors: BankFieldError[] = rawErrors
    .filter((e): e is Record<string, unknown> => !!e && typeof e === 'object')
    .filter((e) => typeof e['source'] === 'string')
    .map((e) => ({
      source: e['source'] as string,
      code: typeof e['code'] === 'string' ? e['code'] : undefined,
      message:
        typeof e['message'] === 'string' && e['message']
          ? (e['message'] as string)
          : 'This field could not be validated',
    }));

  return { message, code, fieldErrors };
}

export function bankingErrorFromHttp(err: HttpErrorResponse): BankingApiError {
  const status = err.status;
  const { message, code, fieldErrors } = parseErrorBody(err.error);

  if (status === 0) {
    return new BankingApiError('Could not reach the server.', 'network', { status });
  }
  if (status === 401 || status === 403) {
    return new BankingApiError(
      status === 403
        ? 'GodMode access is required to manage bank details on behalf of creators.'
        : (message ?? 'Your session has expired.'),
      'auth',
      { status, code },
    );
  }
  if (status >= 500) {
    return new BankingApiError(message ?? 'Server error.', 'server', { status, code });
  }
  if (status === 400) {
    const friendlyMessage =
      code === 'beneficiary_type_unsupported'
        ? 'This payout corridor is not supported by the payment provider for these bank details. For Ireland (EUR), ensure the mailing address is complete. If the problem persists, contact finance — the Airwallex account may need this corridor enabled.'
        : message;
    return new BankingApiError(friendlyMessage ?? 'Your bank details could not be validated.', 'validation', {
      status,
      code,
      fieldErrors,
    });
  }
  return new BankingApiError(message ?? `Request failed (${status}).`, 'unknown', { status, code, fieldErrors });
}

export function toBankingError(err: unknown): BankingApiError {
  if (err instanceof BankingApiError) return err;
  if (err instanceof HttpErrorResponse) return bankingErrorFromHttp(err);
  return new BankingApiError(err instanceof Error ? err.message : 'Something went wrong.', 'unknown');
}

export function loadErrorMessage(kind: BankingErrorKind, status?: number): string {
  switch (kind) {
    case 'network':
      return 'No internet connection. Check your connection and try again.';
    case 'timeout':
      return 'This is taking too long. Please check your connection and try again.';
    case 'auth':
      return status === 403
        ? 'GodMode access is required to view or manage bank details for creators.'
        : 'Your session has expired. Please sign out and back in.';
    default:
      return "We couldn't load bank details right now. Please try again.";
  }
}

export interface MappedBankingFormErrors {
  fieldErrors: Partial<Record<BankingFormFieldKey, string>>;
  banner: string;
}

export function mapApiErrorToFormFields(
  err: BankingApiError,
  variant: BankingRegionVariant,
  step: 'validating' | 'saving',
): MappedBankingFormErrors {
  const notSavedSuffix = step === 'saving' ? ' Bank details were not saved.' : '';

  switch (err.kind) {
    case 'network':
      return { fieldErrors: {}, banner: `No internet connection. Check your connection and try again.${notSavedSuffix}` };
    case 'timeout':
      return {
        fieldErrors: {},
        banner: `This is taking too long. Please check your connection and try again.${notSavedSuffix}`,
      };
    case 'auth':
      return {
        fieldErrors: {},
        banner: err.message || 'Your session has expired. Please sign out and back in, then try again.',
      };
    case 'server':
      return {
        fieldErrors: {},
        banner: 'Something went wrong on our side. Your details were not saved — please try again shortly.',
      };
    case 'validation': {
      if (err.fieldErrors.length === 0) {
        const banner =
          err.code === 'beneficiary_type_unsupported'
            ? 'This payout corridor is not supported for these bank details. For Ireland (EUR), complete the mailing address. If it still fails, contact finance — the Airwallex account may need this corridor enabled.'
            : err.message || `Bank details could not be validated.${notSavedSuffix}`;
        return { fieldErrors: {}, banner };
      }
      const fieldErrors: Partial<Record<BankingFormFieldKey, string>> = {};
      const unmapped: string[] = [];
      for (const fieldErr of err.fieldErrors) {
        const key = fieldKeyForSource(fieldErr.source, variant);
        if (key) {
          fieldErrors[key] ??= fieldErr.message.startsWith('This field could not be validated')
            ? `${BANKING_FIELD_LABELS[key as Exclude<BankingFormFieldKey, 'region'>]} could not be validated${fieldErr.code ? ` (code ${fieldErr.code})` : ''}`
            : fieldErr.message;
        } else {
          unmapped.push(fieldErr.message);
        }
      }
      const mappedCount = Object.keys(fieldErrors).length;
      if (unmapped.length > 0) {
        return { fieldErrors, banner: unmapped.join(' ') };
      }
      if (mappedCount > 0) {
        return { fieldErrors, banner: 'Please fix the highlighted fields below.' };
      }
      return { fieldErrors: {}, banner: err.message || 'Bank details could not be validated.' };
    }
    default:
      return { fieldErrors: {}, banner: `Something unexpected went wrong. Please try again.${notSavedSuffix}` };
  }
}
